'use client';

import type { SafetyWindow } from '@magicschool/business-logic';
import type { Enums } from '@magicschool/supabase/types';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useRoomSchedulingStatus } from '@/features/room/hooks/useRoomSchedulingStatus';
import { useStore } from '@/features/store';
import { useAuth } from '@/hooks/useAuth';

type RoomSchedulingMonitorProps = {
  roomId: string;
  /** The room creator's organization hours, so students move to the locked page when they close. */
  orgWindow: SafetyWindow | null;
};

/**
 * Monitors room scheduling status and navigates when transitions occur.
 * Handles both locked->unlocked (at scheduled start) and unlocked->locked (at scheduled end).
 * This component renders nothing - it only provides the side effect of navigation.
 *
 * Uses useRoomSchedulingStatus directly to ensure the scheduling timer is properly set up.
 */
export const RoomSchedulingMonitor = ({ roomId, orgWindow }: RoomSchedulingMonitorProps) => {
  const router = useRouter();
  const { isLTI } = useAuth();
  const previousStatusRef = useRef<Enums<'room_state'> | null>(null);

  const { currentRoom, followRoomStatus } = useStore(
    ({ StudentStoreData: s }) => ({
      currentRoom: s.currentRoom,
      followRoomStatus: s.followRoomStatus,
    }),
    [],
  );

  // Use the scheduling status hook directly to ensure timer is set up
  const { status } = useRoomSchedulingStatus({
    room: currentRoom,
    orgWindow,
  });

  // Handle status transitions and navigate accordingly
  useEffect(() => {
    // Skip if we don't have a status yet (room not hydrated)
    if (!status) return;

    // Don't track status changes until the room is hydrated.
    if (!currentRoom) {
      return;
    }

    // Initialize the previous status on first run (after room is hydrated)
    if (previousStatusRef.current === null) {
      previousStatusRef.current = status;
      return;
    }

    // Only navigate when status actually changes
    if (previousStatusRef.current !== status) {
      followRoomStatus(roomId, status, router, isLTI);
      previousStatusRef.current = status;
    }
  }, [status, currentRoom, roomId, router, isLTI, followRoomStatus]);

  return null;
};
